import React, { useCallback, useEffect, useMemo } from 'react';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import { useQuery } from 'react-query';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { find, pathOr, propEq } from 'ramda';

import {
  Spinner,
  Tooltip,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import IncomeVerificationForm from './IncomeVerificationForm';
import IncomeVerificationAlternateForm from './IncomeVerificationAlternateForm';
import AssetInformationForm from './AssetInformationForm';

import messages from './messages';
import {
  INITIAL_INCOME_VALUES,
  STATE_INITIAL_INCOME_VALUES,
} from './IncomeVerificationForm/constants';
import { ALTERNATE_FORM_INITIAL_VALUES } from './AssetInformationForm/constants';
import { CERTIFICATION_TYPES } from '../ManageCertifications/constants';

import AffordableQualificationService from '../../services/affordableQualificationService';
import ApplicantService from '../../services/applicantService';
import ApplicantionService from '../../services/applicationService';
import ResidentService from '../../services/residentService';
import StateAffordableProgramConfigService from '../../services/stateAffordableProgramConfigService';
import HouseholdProfileService from '../../services/householdProfileService';

import {
  getCurrentTabAffordableProgram,
  getCertTabFromLocation,
  buildCertNavSearch,
  getIsAltForm,
  getQualificationFormDisabledState,
  getQualificationFormType,
  isReopenModifyHudCertsEnabled,
  normalizeAffordableQualificationChecklistResponse,
  parseAffordableQualificationChecklistValues,
  resolveIncomeAssetApplicationChecklists,
} from './utils';
import usePermissionsHook from '../../components/ElementWithPermissions/usePermissionsHook';
import { shouldUseHudCompletedTabFormReads } from '../GeneralAffordable/isHudCompletedTab';
import {
  filterHouseholdMembers,
  getSettingsByPropertyAndProgram,
} from '../GeneralAffordable/utils';
import { getUrlWithSelectedPropertyId } from '../../utils/navigation-helpers';
import { getURLProgramName, isUnbornChild } from '../../utils/affordable';
import {
  HUD_PROGRAM_NAME,
  PH_PROGRAM_NAME,
} from '../GeneralAffordable/constants';

const QualificationFormsV2 = (props) => {
  const {
    states,
    incomeTypes,
    applicationChecklists,
    isMinor,
    applicantId,
    affordableQualificationId,
    match,
    intl,
    userPermissions,
    isSubmitting,
    isPrinting,
    selectedProperty,
    flags,
    wasAssetFormEdited,
    wasIncomeFormEdited,
    programName,
    actions,
    organizationId,
    propertyId,
    residentId,
    applicationId,
    householdId,
    profile,
    financiallyResponsibleMembersAndMinors,
    history,
    location,
  } = props;

  const hasModifyReopenCertPermission = usePermissionsHook({
    scope: ['hud-qualification-modify-reopen-cert'],
  });

  const qualForm = getQualificationFormType(match?.path);
  const certTab = getCertTabFromLocation(location);
  const affordableProgramSettings = useMemo(
    () =>
      getSettingsByPropertyAndProgram(
        selectedProperty?.setup ?? {},
        programName,
      ),
    [programName, selectedProperty?.setup],
  );
  const autoRolloverIncomeAndAssets =
    affordableProgramSettings.autoRolloverIncomeAndAssets ?? false;
  const useHudCompletedTabSnapshot = shouldUseHudCompletedTabFormReads({
    flags,
    programName,
    certTab,
  });
  const shouldFetchHudLiveChecklist =
    isReopenModifyHudCertsEnabled(flags) &&
    programName === HUD_PROGRAM_NAME &&
    !useHudCompletedTabSnapshot;

  const services = useMemo(() => {
    return {
      stateProgramConfigService: new StateAffordableProgramConfigService(),
      affordableQualificationService: new AffordableQualificationService(),
      applicantService: new ApplicantService(),
      residentService: new ResidentService(),
      applicationService: new ApplicantionService(),
      householdProfileService: new HouseholdProfileService(),
    };
  }, []);

  useEffect(() => {
    actions.getAllStates();
    actions.getAllIncomeTypes();
  }, [actions]);

  const handlePrintIncomeAssetForm = useCallback(
    (type, applicantNameFromForm) => {
      const resolvedApplicantName =
        (typeof applicantNameFromForm === 'string'
          ? applicantNameFromForm
          : '') ||
        financiallyResponsibleMembersAndMinors[applicantId] ||
        '';
      actions.printIncomeAssetForm(
        organizationId,
        applicationId,
        applicantId,
        propertyId,
        affordableQualificationId,
        type,
        resolvedApplicantName,
        programName,
      );
    },
    [
      actions,
      organizationId,
      applicationId,
      applicantId,
      propertyId,
      affordableQualificationId,
      financiallyResponsibleMembersAndMinors,
      programName,
    ],
  );

  const handleIncomeSubmit = useCallback(
    (values, annualIncome) => {
      actions.saveQualificationChecklist(
        {
          incomeChecklist: values,
          annualIncome,
        },
        applicationId,
        applicantId,
        profile,
        residentId,
        affordableQualificationId,
        programName,
        true,
      );
    },
    [
      actions,
      applicationId,
      applicantId,
      profile,
      residentId,
      affordableQualificationId,
      programName,
    ],
  );

  const handleAssetSubmit = useCallback(
    (values, monthlyIncomeFromAssets, imputedIncome) => {
      actions.saveQualificationChecklist(
        {
          assetChecklist: values,
          monthlyIncomeFromAssets,
          imputedIncome,
        },
        applicationId,
        applicantId,
        profile,
        residentId,
        affordableQualificationId,
        programName,
        false,
      );
    },
    [
      actions,
      applicationId,
      applicantId,
      profile,
      residentId,
      affordableQualificationId,
      programName,
    ],
  );

  const goBack = useCallback(() => {
    const urlProgramName = getURLProgramName(programName);
    const id = residentId ? residentId : applicationId;
    history.push(
      getUrlWithSelectedPropertyId(
        `/${profile}/${id}/?tab=${urlProgramName}${buildCertNavSearch(
          location,
        )}`,
      ),
    );
  }, [applicationId, history, location, profile, programName, residentId]);

  const fetchStateProgramHotmaStatus = useCallback(
    async ({ effectiveDate, state }) => {
      return services.stateProgramConfigService.determineHotmaStatus({
        programName,
        state,
        effectiveDate,
      });
    },
    [programName, services.stateProgramConfigService],
  );

  const fetchIncomeAndAssetsCompletionStatuses = useCallback(
    async ({ propertyAffordableProgramId, resolvedHouseholdId }) => {
      if (!resolvedHouseholdId || !propertyAffordableProgramId) {
        return null;
      }

      const data =
        await services.householdProfileService.getHouseholdProfileAffordableQualificationsByProgram(
          organizationId,
          propertyId,
          resolvedHouseholdId,
          propertyAffordableProgramId,
        );
      const filteredAffordableQualifications =
        data.affordableQualifications?.filter(
          ({ qualificationData: { isActive, recertificationStarted } }) =>
            isActive || recertificationStarted,
        );
      const currentAffordableQualification =
        filteredAffordableQualifications?.find(
          ({ qualificationData: { isActive } }) => isActive,
        ) || {};
      const householdMembers = filterHouseholdMembers(
        currentAffordableQualification.eventHouseholdMembers,
      );
      const currentHouseholdMember = householdMembers.find(
        (member) => member.applicantId === applicantId,
      );
      return currentHouseholdMember
        ? {
            completedAssetForm: currentHouseholdMember?.completedAssetForm,
            completedIncomeForm: currentHouseholdMember?.completedIncomeForm,
          }
        : null;
    },
    [applicantId, organizationId, propertyId, services.householdProfileService],
  );

  const fetchResidentIncomeAndAssetsDataFromMostRecentCompletedAQ = useCallback(
    async ({
      affordableSetup,
      currentTabAffordableProgram,
      formCompletionStatuses,
      resolvedHouseholdId,
    }) => {
      const formattedSetup = getSettingsByPropertyAndProgram(
        affordableSetup,
        currentTabAffordableProgram,
      );
      const autofillIncomeOrAssetsForm =
        qualForm === 'incomeVerification'
          ? !formCompletionStatuses?.completedIncomeForm
          : !formCompletionStatuses?.completedAssetForm;

      const autofillForm =
        formattedSetup.autoRolloverIncomeAndAssets &&
        autofillIncomeOrAssetsForm;

      const incomeAndAssetsDataFromMostRecentCompletedAQ = autofillForm
        ? await services.residentService.getResidentIncomeAndAssetsDataFromMostRecentCompletedAQ(
            propertyId,
            residentId,
            applicantId,
            resolvedHouseholdId,
            currentTabAffordableProgram,
          )
        : [];

      return incomeAndAssetsDataFromMostRecentCompletedAQ[0];
    },
    [applicantId, propertyId, qualForm, residentId, services.residentService],
  );

  const fetchData = useCallback(async () => {
    const { physicalState: state } = selectedProperty;
    const aq = await services.affordableQualificationService.getById(
      organizationId,
      propertyId,
      affordableQualificationId,
    );

    const effectiveDate = aq?.voucherEffectiveDate;
    const today = moment();
    const isTodayAfter2024 = today.isAfter('2024-01-01');
    let hotmaStatus = null;

    if (isTodayAfter2024 || !isEmpty(effectiveDate ?? '')) {
      hotmaStatus = await fetchStateProgramHotmaStatus({
        effectiveDate: effectiveDate ?? today.toISOString(),
        state,
      });
    }

    const applicant = await services.applicantService.getApplicant(
      organizationId,
      propertyId,
      applicantId,
    );

    const currentAffordableProgram = getCurrentTabAffordableProgram(
      programName,
      selectedProperty?.pap,
    );

    let incomeAndAssetsDataFromMostRecentCompletedAQ;
    const isRecertification =
      aq?.certificationType === CERTIFICATION_TYPES.RECERT;
    const isCorrection = aq?.isCorrection === true;
    if (
      autoRolloverIncomeAndAssets &&
      isRecertification &&
      !isCorrection &&
      programName !== PH_PROGRAM_NAME
    ) {
      const resolvedHouseholdId = aq?.householdId ?? householdId;
      const formCompletionStatuses =
        await fetchIncomeAndAssetsCompletionStatuses({
          propertyAffordableProgramId: currentAffordableProgram?.id,
          resolvedHouseholdId,
        });

      incomeAndAssetsDataFromMostRecentCompletedAQ = formCompletionStatuses
        ? await fetchResidentIncomeAndAssetsDataFromMostRecentCompletedAQ({
            affordableSetup: selectedProperty.setup,
            currentTabAffordableProgram: programName,
            formCompletionStatuses,
            resolvedHouseholdId,
          })
        : undefined;
    }

    let snapshotApplicationChecklists;
    if (useHudCompletedTabSnapshot) {
      const checklistResponse =
        await services.householdProfileService.getHouseholdApplicantAffordableQualificationChecklist(
          organizationId,
          propertyId,
          aq?.householdId ?? householdId,
          applicantId,
          { isHudCompletedTab: true, affordableQualificationId },
        );
      snapshotApplicationChecklists =
        normalizeAffordableQualificationChecklistResponse(checklistResponse);
    }

    let liveApplicationChecklists;
    if (shouldFetchHudLiveChecklist) {
      const checklistResponse =
        await services.householdProfileService.getHouseholdApplicantAffordableQualificationChecklist(
          organizationId,
          propertyId,
          aq?.householdId ?? householdId,
          applicantId,
          { affordableQualificationId },
        );
      liveApplicationChecklists =
        normalizeAffordableQualificationChecklistResponse(checklistResponse);
    }

    return {
      affordableQualification: aq,
      applicant,
      isHotmaActive: hotmaStatus?.hasAdoptedHOTMA ?? false,
      incomeAndAssetsDataFromMostRecentCompletedAQ,
      snapshotApplicationChecklists,
      liveApplicationChecklists,
    };
  }, [
    affordableQualificationId,
    applicantId,
    autoRolloverIncomeAndAssets,
    fetchIncomeAndAssetsCompletionStatuses,
    fetchResidentIncomeAndAssetsDataFromMostRecentCompletedAQ,
    fetchStateProgramHotmaStatus,
    householdId,
    organizationId,
    programName,
    propertyId,
    selectedProperty,
    services.affordableQualificationService,
    services.applicantService,
    services.householdProfileService,
    shouldFetchHudLiveChecklist,
    useHudCompletedTabSnapshot,
  ]);

  const fetchDataQueryKey = useMemo(
    () => [
      'fetchDataForIncomeAssetForm',
      organizationId,
      propertyId,
      programName,
      affordableQualificationId,
      applicantId,
      residentId,
      householdId,
      applicationId,
      autoRolloverIncomeAndAssets,
      certTab,
      useHudCompletedTabSnapshot,
      shouldFetchHudLiveChecklist,
    ],
    [
      affordableQualificationId,
      applicantId,
      applicationId,
      certTab,
      autoRolloverIncomeAndAssets,
      householdId,
      organizationId,
      programName,
      propertyId,
      residentId,
      shouldFetchHudLiveChecklist,
      useHudCompletedTabSnapshot,
    ],
  );

  const { data, isLoading, isError, refetch } = useQuery(
    fetchDataQueryKey,
    fetchData,
    {
      enabled: Boolean(
        organizationId &&
          propertyId &&
          affordableQualificationId &&
          applicantId,
      ),
      refetchOnWindowFocus: false,
      staleTime:
        isReopenModifyHudCertsEnabled(flags) && programName === HUD_PROGRAM_NAME
          ? 0
          : undefined,
    },
  );

  useEffect(() => {
    if (!data?.applicant) {
      return;
    }

    if (programName === HUD_PROGRAM_NAME && isUnbornChild(data.applicant)) {
      goBack();
    }
  }, [data?.applicant, goBack, programName]);

  const hasCompliancePermission = !!find(
    propEq('scope', 'compliance-approval'),
  )(userPermissions);

  if (isError) {
    return (
      <Grid container align="center" justify="center">
        <Grid item xs={12}>
          <Typography sx={{ fontSize: '12px' }}>
            <FormattedMessage {...messages.loadError} />
            <button onClick={refetch}>
              <FormattedMessage {...messages.retry} />
            </button>
          </Typography>
        </Grid>
      </Grid>
    );
  }

  if (isLoading || !data) {
    return <Spinner />;
  }

  if (programName === HUD_PROGRAM_NAME && isUnbornChild(data.applicant)) {
    return <Spinner />;
  }

  const {
    affordableQualification,
    isHotmaActive,
    incomeAndAssetsDataFromMostRecentCompletedAQ,
    applicant,
  } = data;

  const isCompletedOrReopenedCertification =
    !isNil(affordableQualification?.certificationCompletionDate) ||
    affordableQualification?.certificationStatus === 'REOPEN';

  const currentApplicationChecklists = resolveIncomeAssetApplicationChecklists({
    useHudCompletedTabSnapshot,
    snapshotApplicationChecklists: data.snapshotApplicationChecklists,
    liveApplicationChecklists: data.liveApplicationChecklists,
    reduxApplicationChecklists: shouldFetchHudLiveChecklist
      ? []
      : applicationChecklists,
  });
  const { incomeChecklist, assetChecklist } =
    parseAffordableQualificationChecklistValues({
      currentApplicationChecklists,
      currentQualificationChecklists: affordableQualification?.aqc ?? [],
      applicantId,
      affordableQualificationId,
      incomeAndAssetsDataFromMostRecentCompletedAQ,
      indicators: {
        isHotmaActive,
        isRecertification:
          affordableQualification?.certificationType ===
          CERTIFICATION_TYPES.RECERT,
        preferCurrentCertificationChecklists:
          isCompletedOrReopenedCertification,
      },
    });

  const initialStateValues = {};
  Object.keys(STATE_INITIAL_INCOME_VALUES).forEach((key) => {
    initialStateValues[key] = pathOr(
      '',
      ['value'],
      states.find((s) => s.value !== 'default'),
    );
  });

  const defaultOtherIncomeType = pathOr(
    '',
    ['value'],
    incomeTypes.find((s) => s.value !== 'default'),
  );

  const initialIncomeValues = {
    ...INITIAL_INCOME_VALUES,
    ...initialStateValues,
    ...incomeChecklist,
    otherIncomeType: pathOr(
      defaultOtherIncomeType,
      ['otherIncomeType'],
      incomeChecklist,
    ),
  };

  const initialAssetValues = {
    ...ALTERNATE_FORM_INITIAL_VALUES,
    ...assetChecklist,
    disposedDate: assetChecklist.disposedDate
      ? moment(assetChecklist.disposedDate).format('MM-DD-YYYY')
      : null,
  };

  const isAltForm = getIsAltForm(
    qualForm,
    {
      incomeChecklist,
      assetChecklist,
    },
    flags,
  );

  const filteredIncomeTypes = incomeTypes.filter(
    (x) => !x.programFilter || x.programFilter === programName,
  );

  const { isDisabled, allowReopenedEdit, isDisabledByPermission } =
    getQualificationFormDisabledState({
      affordableQualification,
      flags,
      programName,
      useHudCompletedTabSnapshot,
      hasModifyReopenCertPermission,
    });

  const noPermissionTooltipTitle = isDisabledByPermission
    ? intl.formatMessage(messages.noPermissionAction)
    : '';

  return (
    <Tooltip title={noPermissionTooltipTitle} followCursor>
      <div className="container-fluid padtop30">
        <div className="row">
          <div className="col-xs-6 col-sm-12">
            <a className="btn-text" onClick={goBack}>
              <i className="et-chevron-left" />
              <FormattedMessage {...messages.goBackToProfile} />
            </a>
          </div>
        </div>
        {qualForm === 'incomeVerification' ? (
          isAltForm ? (
            <IncomeVerificationAlternateForm
              intl={intl}
              states={states}
              incomeTypes={filteredIncomeTypes}
              onSubmit={handleIncomeSubmit}
              initialValues={initialIncomeValues}
              applicantName={applicant?.name ?? ''}
              isMinor={isMinor[applicantId]}
              hasCompliancePermission={hasCompliancePermission}
              isSubmitting={isSubmitting}
              handlePrintIncomeAssetForm={(type) =>
                handlePrintIncomeAssetForm(type, applicant?.name)
              }
              selectedProperty={selectedProperty}
              isFormDisabled={isDisabled}
              allowReopenedEdit={allowReopenedEdit}
              flags={flags}
              autoRolloverIncomeAndAssets={autoRolloverIncomeAndAssets}
              isAltForm={isAltForm}
              wasFormEdited={wasIncomeFormEdited}
            />
          ) : (
            <IncomeVerificationForm
              intl={intl}
              states={states}
              incomeTypes={filteredIncomeTypes}
              onSubmit={handleIncomeSubmit}
              initialValues={initialIncomeValues}
              applicantName={applicant?.name ?? ''}
              hasCompliancePermission={hasCompliancePermission}
              isSubmitting={isSubmitting}
              handlePrintIncomeAssetForm={(type) =>
                handlePrintIncomeAssetForm(type, applicant?.name)
              }
              selectedProperty={selectedProperty}
              isFormDisabled={isDisabled}
              allowReopenedEdit={allowReopenedEdit}
            />
          )
        ) : (
          <AssetInformationForm
            intl={intl}
            onSubmit={handleAssetSubmit}
            initialValues={initialAssetValues}
            applicantName={applicant?.name ?? ''}
            hasCompliancePermission={hasCompliancePermission}
            isSubmitting={isSubmitting}
            isPrinting={isPrinting}
            handlePrintIncomeAssetForm={(type) =>
              handlePrintIncomeAssetForm(type, applicant?.name)
            }
            selectedProperty={selectedProperty}
            isAltForm={isAltForm}
            isFormDisabled={isDisabled}
            allowReopenedEdit={allowReopenedEdit}
            isHotmaActive={isHotmaActive}
            flags={flags}
            autoRolloverIncomeAndAssets={autoRolloverIncomeAndAssets}
            wasFormEdited={wasAssetFormEdited}
            effectiveDate={affordableQualification?.voucherEffectiveDate}
            programName={programName}
          />
        )}
      </div>
    </Tooltip>
  );
};

export default QualificationFormsV2;
